// The ground as nested rings of heights (a clipmap), and the one way every view reads it. Wire: groundWire.ts; server: simulation/src/groundwin.py; design: claude/land-truth-design.md (step 3a, phase 2).
//
// The server sends each viewer up to 16 ring windows (stream k: RING_N x RING_N vertices, dx = 0.04 m * 2^k) and a shore window. A window sits on a fixed lattice (the same for every viewer) and stays
// where it is until the eye leaves its middle half, so nothing is re-sampled as the eye moves: the ring is drawn on the window's own lattice, vertex for vertex.
//
// This file holds:
//   * the store: the newest window of each ring, as three array textures (heights as float metres above the sea, the 3 x 3 maximum of the heights, the class byte);
//   * the layout: which part of each ring is drawn. Ring k is drawn from its outer edge in to a hole; the hole is the rectangle ring k - 1 is drawn over (inset from ring k's edge by MORPH_CELLS so the
//     finer ring can blend into the coarser one before they meet, inset to ring k - 1's data, and aligned to ring k's lattice so the edge between them is a line of both). Ring k - 1's heights blend into
//     ring k's (bilinear) over its outer MORPH_CELLS, so on the shared edge both rings are the same line and there is no crack. Everything is whole numbers of 4 cm (`u0`), so the layout is exact;
//   * the lookup every view shares: GROUND_GLSL (`groundYRel`, `groundMaxRel`, `groundSlopeRel`, `groundWeights`, `groundY`) for the shaders, `heightAt` for the CPU. Both pick the finest ring whose drawn rectangle holds
//     the point, so what stands on the ground (the grass, the camera, the sea's depth) is on the surface that is drawn.
//
// Frames: x is three x (east, tile frame); tile y is minus three z. A window's vertex (i, j) is at three (x0 + i dx, -(y0 + j dx)). The shaders count from the floating origin O (origin.ts): `rel` is a position
// minus O, and each ring's offset from O is a uniform made here in doubles.
import * as THREE from 'three';
import { RINGS, RING_N, DX0, type GroundWindow } from './groundWire';

/** Cells of a ring, in from its outer edge, over which its heights blend into the next coarser ring's. */
export const MORPH_CELLS = 12;
/** Cells of a window's edge that are never drawn (the normals and the blend read one cell past a vertex). */
const RING_GUARD = 2;
/** A hole narrower than this many cells of the ring it is cut from is not cut (the finer ring would be a sliver). */
const MIN_HOLE_CELLS = 2;
/** The ring whose lattice (163.84 m) measures the ground's slope for what grows and what colour it is: about the size of the artist's class cell (level 8, 148 m at the patch), which is the scale the slope
 *  thresholds in the land file (`s1`, `s2`) were taken at. Rings finer than this read their slope here; coarser rings read their own. */
export const SLOPE_RING = 12;

/** What the land file says about the ground that the heights cannot: the slope thresholds between gentle, moderate and steep ground (rise over run, at SLOPE_RING's scale), the height of the
 *  coastal flats above the sea, and the sea's level. From the server's ground_info (the artist's block of land.json). */
export interface GroundParams { slopeGentle: number; slopeModerate: number; beachHeight: number; seaLevel: number }

/** [i0, j0, i1, j1]: a rectangle of vertex indices, both ends included. */
export type IRect = [number, number, number, number];

/** A rectangle in u0 (4 cm): x east, y north, both ends included. */
interface URect { x0: number; y0: number; x1: number; y1: number }

export interface RingLayout {
  k: number;
  win: GroundWindow;
  /** the window's origin and its cell, in u0 (4 cm) */
  X0: number; Y0: number; step: number;
  /** the vertices this ring is drawn between (its outer edge) */
  o: IRect;
  /** the rectangle drawn by the finer ring, or null */
  hole: IRect | null;
  /** the lowest and highest height in the window, m */
  lo: number; hi: number;
  /** this ring's vertex (0, 0) in the next coarser ring's cell coordinates; null for the coarsest ring (nothing to blend into) */
  up: [number, number] | null;
}

/** The lookup in GLSL. Needs the uniforms of `GroundClip.uniforms`. `rel` is a position counted from the floating origin (x east, z south = three's axes). */
export const GROUND_GLSL = /* glsl */`
uniform highp sampler2DArray uGH;       // heights, m above the sea (R32F): ring k is layer k, row j = tile y
uniform highp sampler2DArray uGHMax;    // the 3 x 3 maximum of the same
uniform highp sampler2DArray uGC;       // class byte (0 sea, 1 coastal flat, 2 gentle, 3 moderate, 4 steep) / 255
uniform vec4  uGWin[${RINGS}];          // per ring: x, z offset of vertex (0,0) from O (z already negated: j = (w.y - rel.z) * w.z), 1 / dx, n
uniform vec4  uGO[${RINGS}];            // per ring: the drawn rectangle in vertex indices (an empty one, min > max, for a ring that is not used)
uniform int   uGKmin;                   // the finest ring in use
uniform int   uGTop;                    // the coarsest ring in use, -1 when there is no ground yet
uniform vec2  uGOrg;                    // O (x, z)
uniform vec4  uGPar;                    // slope between gentle and moderate ground, between moderate and steep, the coastal flats' height, the sea's level

// the ring that draws rel, and rel in its cell coordinates; beyond all of them the coarsest ring's nearest edge
int groundPick(vec2 rel, out vec2 t) {
  for (int k = uGKmin; k < ${RINGS}; k++) {
    vec4 w = uGWin[k];
    vec2 u = vec2((rel.x - w.x) * w.z, (w.y - rel.y) * w.z);
    vec4 o = uGO[k];
    if (u.x >= o.x && u.y >= o.y && u.x <= o.z && u.y <= o.w) { t = u; return k; }
  }
  if (uGTop < 0) { t = vec2(0.0); return -1; }
  vec4 w = uGWin[uGTop];
  t = clamp(vec2((rel.x - w.x) * w.z, (w.y - rel.y) * w.z), vec2(0.0), vec2(w.w - 1.0001));
  return uGTop;
}
float groundBilinear(int k, vec2 t) {
  t = clamp(t, vec2(0.0), vec2(${(RING_N - 1.0001).toFixed(4)}));
  ivec2 i = ivec2(floor(t));
  vec2 f = t - vec2(i);
  float a = texelFetch(uGH, ivec3(i, k), 0).r;
  float b = texelFetch(uGH, ivec3(i.x + 1, i.y, k), 0).r;
  float c = texelFetch(uGH, ivec3(i.x, i.y + 1, k), 0).r;
  float d = texelFetch(uGH, ivec3(i.x + 1, i.y + 1, k), 0).r;
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
// the ground's height (m above the sea) at rel
float groundYRel(vec2 rel) {
  vec2 t;
  int k = groundPick(rel, t);
  return k < 0 ? 0.0 : groundBilinear(k, t);
}
// an upper bound of groundYRel at rel from one fetch (the 3 x 3 block round the nearest sample holds the four samples the bilinear height is made of)
float groundMaxRel(vec2 rel) {
  vec2 t;
  int k = groundPick(rel, t);
  if (k < 0) return 0.0;
  ivec2 i = clamp(ivec2(floor(t + 0.5)), ivec2(0), ivec2(${RING_N - 1}));
  return texelFetch(uGHMax, ivec3(i, k), 0).r;
}
// the class of the sample nearest rel
float groundClassRel(vec2 rel) {
  vec2 t;
  int k = groundPick(rel, t);
  if (k < 0) return 0.0;
  ivec2 i = clamp(ivec2(floor(t + 0.5)), ivec2(0), ivec2(${RING_N - 1}));
  return floor(texelFetch(uGC, ivec3(i, k), 0).r * 255.0 + 0.5);
}
// The ground's slope (rise over run) at rel, measured across two cells of ring ${SLOPE_RING}'s lattice (or of the drawing ring's own, where that is coarser). A smooth function of position: the heights are bilinear
// in the ring, so the slope has no steps, and a ring finer than ${SLOPE_RING} reads it from the same lattice, so the value does not depend on which ring draws the point (the rings are nested: ring ${SLOPE_RING}'s window holds
// the windows of the finer ones).
float groundSlopeRel(vec2 rel) {
  vec2 t;
  int k = groundPick(rel, t);
  if (k < 0) return 0.0;
  int m = min(max(k, ${SLOPE_RING}), uGTop);
  vec4 w = uGWin[m];
  if (m != k) t = vec2((rel.x - w.x) * w.z, (w.y - rel.y) * w.z);
  float gx = groundBilinear(m, t + vec2(1.0, 0.0)) - groundBilinear(m, t - vec2(1.0, 0.0));
  float gy = groundBilinear(m, t + vec2(0.0, 1.0)) - groundBilinear(m, t - vec2(0.0, 1.0));
  return 0.5 * w.z * length(vec2(gx, gy));                     // w.z is 1 / dx
}
// What kind of land it is, as weights that add up to one: x coastal flat, y gentle, z moderate, w steep. A smooth version of the artist's classes: each class's own colour sits at the middle of its
// slope range (the first at half the first threshold, the last as far above the second threshold as the middle one is above the first) and the weights run linearly between them; the coastal flat is
// gentle ground below the beach height. Not defined below the sea (the caller makes the sea floor its own thing).
vec4 groundWeights(float slope, float h) {
  float s1 = uGPar.x, s2 = uGPar.y;
  float kg = 0.5 * s1, km = 0.5 * (s1 + s2), ks = 1.5 * s2 - 0.5 * s1;
  float a = smoothstep(0.0, 1.0, clamp((slope - kg) / (km - kg), 0.0, 1.0));
  float b = smoothstep(0.0, 1.0, clamp((slope - km) / (ks - km), 0.0, 1.0));
  float gentle = 1.0 - a;
  float beach = gentle * (1.0 - smoothstep(0.0, uGPar.z, h - uGPar.w));
  return vec4(beach, gentle - beach, a * (1.0 - b), a * b);
}
// the same for a position in the world's own coordinates (the world frame loses the float's precision far from the origin: use the Rel forms where O is in play)
float groundY(vec2 xz) { return groundYRel(xz - uGOrg); }
float groundClass(vec2 xz) { return groundClassRel(xz - uGOrg); }
float groundSlope(vec2 xz) { return groundSlopeRel(xz - uGOrg); }
`;

export type GroundUniforms = {
  uGH: { value: THREE.DataArrayTexture };
  uGHMax: { value: THREE.DataArrayTexture };
  uGC: { value: THREE.DataArrayTexture };
  uGWin: { value: THREE.Vector4[] };
  uGO: { value: THREE.Vector4[] };
  uGKmin: { value: number };
  uGTop: { value: number };
  uGOrg: { value: THREE.Vector2 };
  uGPar: { value: THREE.Vector4 };
};

/** The shore window as the sea's solver wants it: heights in a grid starting at (x0, z0) in three's axes (x east, z = -y: row j is at z0 - j * dx), `field.width` = `height` = the window's side. */
export interface ShoreWindow {
  field: { z: Float32Array; nx: number; ny: number; width: number; height: number };
  /** three x and z of the field's sample (0, 0) */
  x0: number; z0: number;
  version: number;
}

const nn = RING_N * RING_N;

function arrayTexture<T extends Float32Array | Uint8Array>(data: T, fmt: THREE.PixelFormat, type: THREE.TextureDataType): THREE.DataArrayTexture {
  const t = new THREE.DataArrayTexture(data as unknown as ConstructorParameters<typeof THREE.DataArrayTexture>[0], RING_N, RING_N, RINGS);
  t.format = fmt; t.type = type;
  t.minFilter = t.magFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  return t;
}

export class GroundClip {
  private readonly hData = new Float32Array(nn * RINGS);
  private readonly mData = new Float32Array(nn * RINGS);
  private readonly cData = new Uint8Array(nn * RINGS);
  readonly uniforms: GroundUniforms;
  private win: (GroundWindow | null)[] = new Array(RINGS).fill(null);
  private range: [number, number][] = Array.from({ length: RINGS }, () => [0, 0] as [number, number]);
  private rings: (RingLayout | null)[] = new Array(RINGS).fill(null);
  private dirty = false;
  private lastOx = NaN; private lastOz = NaN;
  private tmp = new Float32Array(nn);
  /** Bumped when the layout changes (a ring arrived or a window moved): the meshes redo their index buffers. */
  layoutVersion = 0;
  private shoreWin: GroundWindow | null = null;
  private shoreCache: ShoreWindow | null = null;
  private lowest = Infinity;
  /** windows taken since construction (a counter for the overlay and the tests) */
  taken = 0;

  constructor() {
    const wins: THREE.Vector4[] = [], os: THREE.Vector4[] = [];
    for (let k = 0; k < RINGS; k++) { wins.push(new THREE.Vector4(0, 0, 1, RING_N)); os.push(new THREE.Vector4(1, 1, 0, 0)); }
    this.uniforms = {
      uGH: { value: arrayTexture(this.hData, THREE.RedFormat, THREE.FloatType) },
      uGHMax: { value: arrayTexture(this.mData, THREE.RedFormat, THREE.FloatType) },
      uGC: { value: arrayTexture(this.cData, THREE.RedFormat, THREE.UnsignedByteType) },
      uGWin: { value: wins }, uGO: { value: os },
      uGKmin: { value: RINGS }, uGTop: { value: -1 },
      uGOrg: { value: new THREE.Vector2() },
      uGPar: { value: new THREE.Vector4(NaN, NaN, NaN, NaN) },      // set by setParams before any ground is drawn (TerrainView refuses to draw without it)
    };
  }

  /** What the land file says about the ground (see GroundParams); the colour of the ground and what grows on it depend on it. */
  setParams(p: GroundParams): void {
    if (!(p.slopeGentle > 0 && p.slopeModerate > p.slopeGentle && p.beachHeight > 0 && Number.isFinite(p.seaLevel))) {
      throw new Error(`ground parameters are not usable: ${JSON.stringify(p)} (want 0 < slopeGentle < slopeModerate, beachHeight > 0, a finite sea level)`);
    }
    this.uniforms.uGPar.value.set(p.slopeGentle, p.slopeModerate, p.beachHeight, p.seaLevel);
  }
  /** True once setParams has been called. */
  get hasParams(): boolean { return Number.isFinite(this.uniforms.uGPar.value.x); }

  /** True once there is any ground to draw. */
  get ready(): boolean { return this.uniforms.uGTop.value >= 0; }
  /** The lowest ground seen in any window (m above the sea); Infinity before the first. */
  get minHeight(): number { return this.lowest; }
  /** The layout of ring k, or null when it is not in use. */
  ring(k: number): RingLayout | null { return this.rings[k] ?? null; }
  /** The finest ring in use (RINGS when none) and the coarsest (-1 when none). */
  get kmin(): number { return this.uniforms.uGKmin.value; }
  get top(): number { return this.uniforms.uGTop.value; }
  /** The rings that have a window, finest first (for the overlay). */
  have(): number[] { const r: number[] = []; for (let k = 0; k < RINGS; k++) if (this.win[k]) r.push(k); return r; }
  windowOf(k: number): GroundWindow | null { return this.win[k] ?? null; }

  /** Take a window from the wire. A ring's window replaces the ring's last; the shore window replaces the shore's. */
  accept(w: GroundWindow): void {
    const m = w.meta, s = m.stream;
    this.taken++;
    if (s >= RINGS) { this.shoreWin = w; this.shoreCache = null; return; }
    if (s < 0 || m.n !== RING_N || Math.abs(m.dx - DX0 * 2 ** s) > 1e-9) throw new Error(`ground window of stream ${s}: n ${m.n}, dx ${m.dx} is not a ring of this client`);
    this.win[s] = w;
    const h = this.hData.subarray(s * nn, (s + 1) * nn), c = this.cData.subarray(s * nn, (s + 1) * nn);
    const base = m.base, sc = m.scale;
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < nn; i++) { const v = base + sc * w.q[i]!; h[i] = v; if (v < lo) lo = v; if (v > hi) hi = v; }
    this.range[s] = [lo, hi];
    c.set(w.cls);
    if (lo < this.lowest) this.lowest = lo;
    this.max3(h, this.mData.subarray(s * nn, (s + 1) * nn));
    this.dirty = true;
  }

  /** the 3 x 3 maximum of a ring (edges clamp), separably */
  private max3(h: Float32Array, out: Float32Array): void {
    const n = RING_N, t = this.tmp;
    for (let j = 0; j < n; j++) {
      const r = j * n;
      for (let i = 0; i < n; i++) {
        let v = h[r + i]!;
        if (i > 0 && h[r + i - 1]! > v) v = h[r + i - 1]!;
        if (i < n - 1 && h[r + i + 1]! > v) v = h[r + i + 1]!;
        t[r + i] = v;
      }
    }
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        let v = t[j * n + i]!;
        if (j > 0 && t[(j - 1) * n + i]! > v) v = t[(j - 1) * n + i]!;
        if (j < n - 1 && t[(j + 1) * n + i]! > v) v = t[(j + 1) * n + i]!;
        out[j * n + i] = v;
      }
    }
  }

  /**
   * Once per frame, before drawing: work the layout out again if a window came, upload the textures, and put each ring's offset from the floating origin (ox, oz) into the uniforms.
   * Returns true when the layout changed.
   */
  sync(ox: number, oz: number): boolean {
    let changed = false;
    if (this.dirty) {
      this.dirty = false;
      this.relayout();
      this.uniforms.uGH.value.needsUpdate = true;
      this.uniforms.uGHMax.value.needsUpdate = true;
      this.uniforms.uGC.value.needsUpdate = true;
      this.layoutVersion++;
      changed = true;
    }
    if (changed || ox !== this.lastOx || oz !== this.lastOz) {
      this.lastOx = ox; this.lastOz = oz;
      this.uniforms.uGOrg.value.set(ox, oz);
      const wins = this.uniforms.uGWin.value, os = this.uniforms.uGO.value;
      for (let k = 0; k < RINGS; k++) {
        const r = this.rings[k];
        if (!r) { os[k]!.set(1, 1, 0, 0); continue; }
        const m = r.win.meta;
        wins[k]!.set(m.x0 - ox, -m.y0 - oz, 1 / m.dx, RING_N);
        os[k]!.set(r.o[0], r.o[1], r.o[2], r.o[3]);
      }
    }
    return changed;
  }

  private relayout(): void {
    const win = this.win;
    const out: (RingLayout | null)[] = new Array(RINGS).fill(null);
    let top = -1;
    for (let k = RINGS - 1; k >= 0; k--) if (win[k]) { top = k; break; }
    let kmin = RINGS;
    if (top >= 0) {
      const info = (k: number) => { const m = win[k]!.meta; return { X0: Math.round(m.x0 / DX0), Y0: Math.round(m.y0 / DX0), step: 2 ** k }; };
      let a = info(top);
      let ext = (RING_N - 1) * a.step;
      // the rectangle in u0 that ring k is drawn over: for the coarsest ring, its whole window
      let rect: URect | null = { x0: a.X0, y0: a.Y0, x1: a.X0 + ext, y1: a.Y0 + ext };
      let upInfo: ReturnType<typeof info> | null = null;
      for (let k = top; k >= 0 && rect; k--) {
        a = info(k);
        ext = (RING_N - 1) * a.step;
        const o: IRect = [(rect.x0 - a.X0) / a.step, (rect.y0 - a.Y0) / a.step, (rect.x1 - a.X0) / a.step, (rect.y1 - a.Y0) / a.step];
        let hole: IRect | null = null;
        let next: URect | null = null;
        if (k > 0 && win[k - 1]) {
          const b = info(k - 1);
          const inset = k < top ? MORPH_CELLS * a.step : 0;                   // only a ring that blends into a coarser one needs room for the blend
          const up = (v: number) => Math.ceil(v / a.step) * a.step, down = (v: number) => Math.floor(v / a.step) * a.step;
          const x0 = up(Math.max(rect.x0 + inset, b.X0 + RING_GUARD * b.step)), x1 = down(Math.min(rect.x1 - inset, b.X0 + (RING_N - 1 - RING_GUARD) * b.step));
          const y0 = up(Math.max(rect.y0 + inset, b.Y0 + RING_GUARD * b.step)), y1 = down(Math.min(rect.y1 - inset, b.Y0 + (RING_N - 1 - RING_GUARD) * b.step));
          if (x1 - x0 >= MIN_HOLE_CELLS * a.step && y1 - y0 >= MIN_HOLE_CELLS * a.step) {
            next = { x0, y0, x1, y1 };
            hole = [(x0 - a.X0) / a.step, (y0 - a.Y0) / a.step, (x1 - a.X0) / a.step, (y1 - a.Y0) / a.step];
          }
        }
        out[k] = {
          k, win: win[k]!, X0: a.X0, Y0: a.Y0, step: a.step, o, hole, lo: this.range[k]![0], hi: this.range[k]![1],
          up: upInfo ? [(a.X0 - upInfo.X0) / upInfo.step, (a.Y0 - upInfo.Y0) / upInfo.step] : null,
        };
        kmin = k;
        upInfo = a;
        rect = next;
      }
    }
    this.rings = out;
    this.uniforms.uGKmin.value = kmin;
    this.uniforms.uGTop.value = top;
  }

  /** The ground's height (m above the sea) at three (x, z), from the finest ring that draws it. 0 before there is any ground. */
  heightAt(x: number, z: number): number {
    const top = this.uniforms.uGTop.value;
    if (top < 0) return 0;
    for (let k = this.uniforms.uGKmin.value; k <= top; k++) {
      const r = this.rings[k];
      if (!r) continue;
      const m = r.win.meta;
      const i = (x - m.x0) / m.dx, j = (-z - m.y0) / m.dx;
      if (i >= r.o[0] && j >= r.o[1] && i <= r.o[2] && j <= r.o[3]) return this.bilinear(r, i, j);
    }
    const r = this.rings[top]!;
    const m = r.win.meta;
    return this.bilinear(r, (x - m.x0) / m.dx, (-z - m.y0) / m.dx);
  }

  private bilinear(r: RingLayout, i: number, j: number): number {
    const n = RING_N;
    i = Math.min(Math.max(i, 0), n - 1.0001); j = Math.min(Math.max(j, 0), n - 1.0001);
    const i0 = Math.floor(i), j0 = Math.floor(j), fx = i - i0, fy = j - j0;
    const q = r.win.q, m = r.win.meta;
    const k = j0 * n + i0;
    const v = (q[k]! * (1 - fx) + q[k + 1]! * fx) * (1 - fy) + (q[k + n]! * (1 - fx) + q[k + n + 1]! * fx) * fy;
    return m.base + m.scale * v;
  }

  /** The class (0 to 4) of the sample nearest three (x, z), or 0. */
  classAt(x: number, z: number): number {
    const top = this.uniforms.uGTop.value;
    if (top < 0) return 0;
    for (let k = this.uniforms.uGKmin.value; k <= top; k++) {
      const r = this.rings[k];
      if (!r) continue;
      const m = r.win.meta;
      const i = (x - m.x0) / m.dx, j = (-z - m.y0) / m.dx;
      if (i >= r.o[0] && j >= r.o[1] && i <= r.o[2] && j <= r.o[3]) return r.win.cls[Math.round(j) * RING_N + Math.round(i)]!;
    }
    return 0;
  }

  /** The shore window as a height grid for the sea's beach-wave solver, or null if the server has not sent one (the eye is high, or it has not come yet). Cached until a new window comes. */
  shore(): ShoreWindow | null {
    const w = this.shoreWin;
    if (!w) return null;
    if (this.shoreCache) return this.shoreCache;
    const m = w.meta, n = m.n;
    const z = new Float32Array(n * n);
    for (let i = 0; i < z.length; i++) z[i] = m.base + m.scale * w.q[i]!;
    const side = (n - 1) * m.dx;
    this.shoreCache = { field: { z, nx: n, ny: n, width: side, height: side }, x0: m.x0, z0: -m.y0, version: ++this.shoreSerial };
    return this.shoreCache;
  }
  private shoreSerial = 0;

  /** Forget everything (a new connection streams the ground again from nothing). */
  reset(): void {
    this.win.fill(null); this.rings.fill(null);
    this.shoreWin = null; this.shoreCache = null;
    this.lowest = Infinity; this.dirty = false;
    this.uniforms.uGKmin.value = RINGS; this.uniforms.uGTop.value = -1;
    for (let k = 0; k < RINGS; k++) this.uniforms.uGO.value[k]!.set(1, 1, 0, 0);
    this.lastOx = NaN; this.lastOz = NaN;
    this.layoutVersion++;
  }

  dispose(): void {
    this.uniforms.uGH.value.dispose(); this.uniforms.uGHMax.value.dispose(); this.uniforms.uGC.value.dispose();
  }
}
